/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { LabelList, RadialBar, RadialBarChart } from "recharts"

import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/Components/ui/card"
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "@/Components/ui/chart"
import { useNavigate } from "react-router-dom"
import { useIsMobile } from "@/hooks/use-mobile"

const chartConfig = {
    Percentage: {
        label: "Progress",
        color: "hsl(var(--card))",
    },
    Planned: {
        label: "Planned Progress",
        color: "hsl(var(--chart-planned))",
    },
    Actual: {
        label: "Actual Progress",
        color: "hsl(var(--chart-actual))",
    },
} satisfies ChartConfig

export interface RadialChartProps {
    plannedPercentage: number
    actualPercentage: number
    isMultiProject?: boolean
    onClick?: () => void
    isMobile?: boolean
}

export function RadialChartComponent({ plannedPercentage, actualPercentage, isMultiProject = false, onClick }: RadialChartProps) {
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const chartData = [
        { progress: "", Percentage: 100, fill: "hsl(var(--card))", stroke: "hsl(var(--card))" }, // Match card background
        { progress: "Planned Progress", Percentage: plannedPercentage, fill: "hsl(var(--chart-planned))" },
        { progress: "Actual Progress", Percentage: actualPercentage, fill: "hsl(var(--chart-actual))" },
    ]

    function handleClick() {
        if (onClick) {
            onClick();
        } else {
            navigate("/Plan");
        }
    }

    // Custom tooltip that filters out the hidden bar
    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null;
        // Don't show tooltip for the hidden bar (empty progress)
        if (payload[0]?.payload?.progress === "") return null;

        return <ChartTooltipContent hideLabel nameKey="progress" active={active} payload={payload} />;
    }

    return (
        <Card id="radialChartCard" className={`flex justify-center flex-col ${isMobile ? 'w-full' : 'min-w-[300px] mx-auto'}`}>
            <style>{`
                .hide-first-bar-hover .recharts-layer:first-child .recharts-radial-bar-sector {
                    pointer-events: none !important;
                }
                .hide-first-bar-hover .recharts-radial-bar-sector:first-child {
                    pointer-events: none !important;
                }
            `}</style>
            <CardHeader className="items-center pb-2">
                <CardTitle className={`${isMobile ? 'text-4xl' : 'text-xl'}`}>{isMultiProject ? "Overall Progress" : "Project Progress"}</CardTitle>

            </CardHeader>
            <CardContent className="flex pb-2 relative isolate">
                <div className="relative w-full">
                    <ChartContainer
                        config={chartConfig}
                        className={`mx-auto aspect-square ${isMobile ? 'max-h-[500px]' : 'max-h-[250px]'} w-full relative hide-first-bar-hover`}
                    >
                        <RadialBarChart
                            data={chartData}
                            startAngle={90}
                            endAngle={-270}
                            innerRadius={isMobile ? 80 : 40}
                            outerRadius={isMobile ? 240 : 120}
                            onClick={() => handleClick()}
                        >
                            <ChartTooltip
                                cursor={false}
                                content={<CustomTooltip />}
                                wrapperStyle={{ zIndex: 9999 }}
                            />
                            <RadialBar dataKey="Percentage" background isAnimationActive={false}>
                                <LabelList
                                    position="insideStart"
                                    dataKey="progress"
                                    className="fill-white capitalize mix-blend-luminosity"
                                    fontSize={isMobile ? 22 : 11}
                                />
                            </RadialBar>
                        </RadialBarChart>
                    </ChartContainer>
                    {/* Overlay to cover the inner bar and show percentages */}
                    <div className={`absolute ${isMobile ? 'top-[30%] w-[200px] h-[200px]' : 'top-[30%] w-[100px] h-[100px]'} left-1/2 -translate-x-1/2 flex flex-col items-center rounded-full bg-chart-radial-center justify-center pointer-events-none z-[1]`}>
                        <div className={`${isMobile ? 'text-4xl' : 'text-xl font-bold'} text-chart-planned`}>{chartData[1].Percentage}%</div>
                        <div className={`${isMobile ? 'text-4xl' : 'text-xl font-bold'} text-chart-actual`}>{chartData[2].Percentage}%</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}
